import React, { useState } from 'react';
import { Box, Container, Typography, Button, Snackbar, Alert, Autocomplete, TextField } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pracaService } from '../services/praca';
import { produtoService } from '../services/produto';
import DatePickerField from '../components/common/DatePickerField';
import { relatoriosSaidaService } from '../services/relatoriosSaida';

const RelatorioVendasPorPeriodoReferencia: React.FC = () => {
  const navigate = useNavigate();
  const [idPraca, setIdPraca] = useState<number | null>(null);
  const [produtosSelecionados, setProdutosSelecionados] = useState<any[]>([]);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'info' | 'error' });

  const { data: pracas = [] } = useQuery({
    queryKey: ['pracas'],
    queryFn: pracaService.getAll,
  });

  const { data: produtos = [] } = useQuery({
    queryKey: ['produtos', 'select'],
    queryFn: produtoService.getForSelect,
  });

  const pracaSelecionada = pracas.find((p) => p.id === idPraca) || null;

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getProdutoId = (produto: any): number => {
    return Number(produto.idProduto ?? produto.id ?? produto.IdProduto ?? 0);
  };

  const handleGerar = async () => {
    if (!idPraca || !dataInicio || !dataFim || produtosSelecionados.length === 0) {
      setSnackbar({ open: true, message: 'Selecione a praça, as datas e pelo menos um produto.', severity: 'error' });
      return;
    }
    setLoading(true);
    setSnackbar({ open: true, message: 'Gerando arquivo para impressão. Aguarde!', severity: 'info' });
    try {
      const blob = await relatoriosSaidaService.imprimirVendasPorReferenciaPeriodo({
        dataInicioCobranca: dataInicio,
        dataFimCobranca: dataFim,
        idPraca,
        idsProdutos: produtosSelecionados.map(getProdutoId),
      });
      downloadBlob(blob, `${dataInicio}_${dataFim}.xlsx`);
      setSnackbar({ open: true, message: 'Relatório gerado com sucesso.', severity: 'info' });
    } catch (error: any) {
      setSnackbar({ open: true, message: 'Erro ao gerar relatório: ' + (error.message || error), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    navigate('/Relatorios');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Vendas por Período e Referência
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleVoltar}>
          Voltar
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 500 }}>
        <Autocomplete
          options={pracas}
          getOptionLabel={(p) => p.nomePraca}
          value={pracaSelecionada}
          onChange={(_, value) => setIdPraca(value ? value.id : null)}
          renderInput={(params) => <TextField {...params} label="Praça" placeholder="Selecione a praça" />}
        />

        <Autocomplete
          multiple
          options={produtos}
          getOptionLabel={(p) => p.informacoesExibir || `${p.referencia || ''} - ${p.nomeProduto || p.nome || ''}`}
          value={produtosSelecionados}
          onChange={(_, value) => setProdutosSelecionados(value)}
          renderInput={(params) => <TextField {...params} label="Produtos" placeholder="Selecione os produtos" />}
        />

        <DatePickerField label="Data início retorno" value={dataInicio} onChange={setDataInicio} />
        <DatePickerField label="Data fim retorno" value={dataFim} onChange={setDataFim} />

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleGerar} disabled={loading}>
            Gerar relatório
          </Button>
          <Button variant="outlined" onClick={handleVoltar} disabled={loading}>
            Voltar
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default RelatorioVendasPorPeriodoReferencia;
